import { UserRole } from '@/lib/types';

export type TRoute = {
  href: ERoute | ERouteAction;
  permission: UserRole[];
  parentHref?: ERoute;
};

export enum ERoute {
  DASHBOARD = 'dashboard',
  ACCOUNT = 'account',
  PROFILE = 'profile',
  PAYMENT_HISTORY = 'paymenthistory',
  SETTINGS = 'settings',
  ANIMALS = 'animals',
  MEMBERS = 'members',
  RULES = 'rules',
  ADD_ANIMAL = 'addanimal',
  RESTRICTED = 'restricted',
  ORGANISATION = 'organisation',
  ADD_ORG_TRAIT = 'addorganisationtrait',
  CONFIG = 'config',
  BECOME_MEMBER = 'becomemember',
  ADD_MEMBER = 'add-member/details',
  BUILDER = 'builder',
  FINISH_ACCOUNT = 'finish-account-setup',
  FIELDS_CONFIG = 'fields',
  FORMS_MANAGER = 'forms',
  TRANSFERS = 'transfers',
  IMPORTS = 'imports',
  CERTIFICATE_TEMPLATES = 'certificates',
  USER_ASSIGNMENTS = 'user-assignments',
  SOLR_SYNC = 'solr-sync',
}

export const APP_ROUTE_NAMES = {
  HOME: 'Home',
  SEARCH: 'Database Search',
  REGISTRY: 'Registry',
};

export const PRIVATE_TOP_LEVEL_SEGMENTS: readonly string[] = [
  'dashboard',
  'pendingorders',
  'account',
  'entity-query',
  'restricted',
];

export const PUBLIC_ROUTES = {
  HOME: '/',
  /** Org-picker landing (public marketing header). */
  SEARCH: '/search',
  ACCEPT_INVITE: '/accept-invite',
  HEALTHZ: '/healthz',
  LOGOUT: '/logout',
  RESTRICTED: '/restricted',
  SUPPORT: '/support',
  TERMS: '/terms',
  PRIVACY: '/privacy',
  /**
   * Friendly fallback page for auth-flow failures (e.g. misconfigured
   * Entra/OIDC env vars). Linked to by the auth route handler when a
   * browser navigation hits an unrecoverable auth-side error.
   */
  AUTH_ERROR: '/auth-error',
  /** Society public animal search + detail: `/{societyCode}/animals`. */
  ANIMAL: '/animals',
  /** Society public member search + detail: `/{societyCode}/members`. */
  MEMBER: '/members',
  REGISTRY_LANDING: '/registry',
};

export const APP_ROUTE_PATHS = {
  DASHBOARD: `/${ERoute.DASHBOARD}`,
  FORMS_MANANGER: `/${ERoute.CONFIG}/${ERoute.FORMS_MANAGER}`,
  CREATE_FORM: `/${ERoute.CONFIG}/${ERoute.FORMS_MANAGER}/create`,
  FIELDS_CONFIG: `/${ERoute.CONFIG}/${ERoute.FIELDS_CONFIG}`,
  ANIMALS: `/${ERoute.ANIMALS}`,
  IMPORTS: `/${ERoute.IMPORTS}`,
  MEMBERS: `/${ERoute.MEMBERS}`,
  RULES: `/${ERoute.CONFIG}/${ERoute.RULES}`,
  RULE_TEST_RUNNER: `/${ERoute.CONFIG}/${ERoute.RULES}/test-runner`,
  CREATE_ANIMAL: `/${ERoute.ANIMALS}/create`,
  CREATE_MEMBER: `/${ERoute.MEMBERS}/create`,
  ADD_MEMBER: `/${ERoute.MEMBERS}/${ERoute.ADD_MEMBER}`,
  TRANSFERS: `/${ERoute.ANIMALS}/${ERoute.TRANSFERS}`,
  CERTIFICATE_TEMPLATES: `/${ERoute.CONFIG}/${ERoute.CERTIFICATE_TEMPLATES}`,
  USER_ASSIGNMENTS: `/${ERoute.USER_ASSIGNMENTS}`,
  SOLR_SYNC: `/${ERoute.SOLR_SYNC}`,
};

export enum ERouteAction {
  VIEW = 'view',
  SELECT = 'select',
  REVIEW = 'review',
}

/**
 * Per-segment role requirements for top-level registry routes
 * (`/[orgName]/registry/<segment>/...`).
 *
 * Segments that are not listed here are accessible to any member of the org.
 * To restrict a new registry route, add it here.
 */
export const REGISTRY_SEGMENT_PERMISSIONS: Partial<
  Record<ERoute, ReadonlyArray<UserRole>>
> = {
  // Society admins and Breedlogic admins can manage org-level config.
  [ERoute.CONFIG]: [UserRole.SocietyAdmin, UserRole.Admin],
  // User assignments are managed by Breedlogic admins only.
  [ERoute.USER_ASSIGNMENTS]: [UserRole.Admin],
  // Solr sync is a Breedlogic admin-only maintenance tool.
  [ERoute.SOLR_SYNC]: [UserRole.Admin],
};

/**
 * Returns the roles allowed to access a given registry pathname, or `null`
 * if the path has no restriction (or isn't under the registry prefix).
 */
export const getRegistryPathAllowedRoles = (
  pathname: string,
  orgName: string,
): ReadonlyArray<UserRole> | null => {
  const prefix = `/${orgName}/registry/`;
  if (!pathname.startsWith(prefix)) return null;
  const segment = pathname.slice(prefix.length).split('/')[0] as ERoute;
  return REGISTRY_SEGMENT_PERMISSIONS[segment] ?? null;
};

/**
 * Returns true if a user holding any of the given `roleIds` is allowed to
 * access the registry `pathname`. Unrestricted paths return true.
 *
 * Accepts a list because a user may hold several roles in one org (e.g. a
 * Breedlogic Admin granted both via wildcard and as Society Admin); the
 * single "primary" role id can hide the higher-privilege one.
 *
 * For backwards compatibility a single id is also accepted.
 */
export const canAccessRegistryPath = (
  pathname: string,
  orgName: string,
  roleIds: number | number[] | null | undefined,
): boolean => {
  const allowed = getRegistryPathAllowedRoles(pathname, orgName);
  if (!allowed) return true;

  const ids =
    roleIds == null ? [] : Array.isArray(roleIds) ? roleIds : [roleIds];

  return ids.some((id) => allowed.includes(id as UserRole));
};

const allPermissions = [
  UserRole.Admin,
  UserRole.Breeder,
  UserRole.SocietyAdmin,
];

export const routes: Array<TRoute> = [
  {
    href: ERoute.RESTRICTED,
    permission: allPermissions,
  },
  {
    href: ERoute.ACCOUNT,
    permission: allPermissions,
  },
  {
    href: ERoute.PROFILE,
    parentHref: ERoute.ACCOUNT,
    permission: allPermissions,
  },
  {
    href: ERoute.PAYMENT_HISTORY,
    parentHref: ERoute.ACCOUNT,
    permission: allPermissions,
  },
  {
    href: ERoute.SETTINGS,
    parentHref: ERoute.ACCOUNT,
    permission: allPermissions,
  },
  {
    href: ERoute.ANIMALS,
    permission: allPermissions,
  },
  {
    href: ERoute.MEMBERS,
    permission: allPermissions,
  },
  {
    href: ERoute.ADD_ANIMAL,
    permission: allPermissions,
  },
  {
    href: ERoute.ORGANISATION,
    permission: allPermissions,
  },
  {
    href: ERoute.ADD_ORG_TRAIT,
    permission: allPermissions,
  },
  {
    href: ERoute.CONFIG,
    parentHref: ERoute.ORGANISATION,
    permission: [UserRole.Admin],
  },
  {
    href: ERoute.BECOME_MEMBER,
    permission: allPermissions,
  },
  {
    href: ERoute.BUILDER,
    permission: allPermissions,
  },
  {
    href: ERoute.FINISH_ACCOUNT,
    permission: allPermissions,
  },
];
